import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { validatePhoneNumber, validateOtp } from "../../utils/validation";
import { sendPhoneNumber, verifyOtp } from "../../services/api";
import { AuthContext } from "../../context/AuthContext";
import SignUpForm from "./SignUpForm";
import { useDeliverySession } from "../../hooks/useDeliverySession";
import { formatDate } from "../../utils/dateUtils";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

const SignInForm = ({ onClose }) => {
  const { login, isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // State variables
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [apiError, setApiError] = useState("");
  const [userPhoneNumber, setUserPhoneNumber] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [user_type, setUserType] = useState("resident");
  const [showModal, setShowModal] = useState(false);
  const [fullName, setFullName] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(true);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { updateDeliveryField, session } = useDeliverySession();

  const activeInputRef = useRef(null);
  // Use the custom hook to lock body scroll
  useLockBodyScroll(true);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (otpResendTimer > 0) {
      interval = setInterval(() => {
        setOtpResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (otpResendTimer === 0 && isResendDisabled) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [otpResendTimer, isResendDisabled]);

  const handlePhoneChange = (e) => {
    const numericText = e.target.value.replace(/[^0-9]/g, "");
    setPhoneNumber(numericText);
    setPhoneNumberError("");
    setApiError("");
  };

  const handleCreateAccount = () => {
    console.log("Account Created:", { fullName, phoneNumber });
    console.log("encryptData:-", user);
    console.log("📦 Current Delivery Session JSON:", session);

    const created_on = formatDate(user.created_on);

    updateDeliveryField("da_id", user.id);
    updateDeliveryField("da_name", fullName);
    updateDeliveryField("login_id", user.login_id);
    updateDeliveryField("created_at", user.created_at);
    updateDeliveryField("updated_at", user.updated_at);
    updateDeliveryField("finished_at", user.finished_at);
    updateDeliveryField("phone_number", user.phone_number);
    console.log("Final session:", session);

    setTimeout(() => {
      setIsRedirecting(true);
      navigate("/delivery-dashboard", { replace: true });
      setIsRedirecting(false);
    }, 2000);
  };

  const handleSendOtp = async () => {
    const error = validatePhoneNumber(phoneNumber);
    if (error) {
      setPhoneNumberError(error);
      return;
    }

    setIsLoading(true);
    setApiError("");
    setSuccessMessage("");

    try {
      const response = await sendPhoneNumber(phoneNumber, user_type);
      setIsOtpSent(true);
      setSuccessMessage(response.message || "OTP sent successfully!");
      setOtp(["", "", "", "", "", ""]); // Reset OTP fields
      setOtpResendTimer(30);
      setIsResendDisabled(true);
    } catch (error) {
      setApiError(
        error.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newOtp = pastedData.padEnd(6, "").split("");
    setOtp(newOtp);
    setOtpError("");

    // Focus on the last input after pasting
    const lastInput = document.getElementById(`otp-${5}`);
    if (lastInput) lastInput.focus();
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length === 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setOtpError("");

      // Auto-focus next input
      if (index < 5) {
        setTimeout(() => {
          const nextInput = document.getElementById(`otp-${index + 1}`);
          if (nextInput) nextInput.focus();
        }, 10);
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && index === 0 && otp[index] === "") {
      return; // Prevent moving focus if first box is empty
    }
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index] === "" && index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        const prev = document.getElementById(`otp-${index - 1}`);
        if (prev) prev.focus();
      } else {
        newOtp[index] = "";
        setOtp(newOtp);
      }
      setOtpError("");
    }
  };

  const handleResendOtp = async () => {
    const error = validatePhoneNumber(phoneNumber);
    if (error) {
      setPhoneNumberError(error);
      return;
    }

    setIsLoading(true);
    setApiError("");
    setSuccessMessage("");

    try {
      const response = await sendPhoneNumber(phoneNumber, user_type);
      setOtp(["", "", "", "", "", ""]); // Reset OTP fields
      setSuccessMessage(response.message || "OTP resent successfully!");
      setOtpResendTimer(30);
      setIsResendDisabled(true);
    } catch (error) {
      setApiError(
        error.response?.data?.message ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleIndividualPaste = (e, startIndex) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6 - startIndex);

    if (pastedData.length === 0) return;

    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length && startIndex + i < 6; i++) {
      newOtp[startIndex + i] = pastedData[i];
    }

    setOtp(newOtp);
    setOtpError("");

    // Focus on the last filled input
    const lastFilledIndex = Math.min(startIndex + pastedData.length - 1, 5);
    setTimeout(() => {
      const inputToFocus = document.getElementById(`otp-${lastFilledIndex}`);
      if (inputToFocus) inputToFocus.focus();
    }, 10);
  };

  const handleLogin = async () => {
    const enteredOtp = otp.join("");
    const error = validateOtp(enteredOtp);
    if (error) {
      setIsOtpValid(error);
      return;
    }

    try {
      const response = await verifyOtp(phoneNumber.toString(), enteredOtp); // ✅ OTP verification API

      if (response.success === true) {
        setApiError("");

        setSuccessMessage(`Login successful! ${response.message}`);

        const {
          access,
          refresh,
          resident_data,
          member_data,
          user_type,
          delivery_data,
        } = response.data;
        const userData = resident_data || member_data || delivery_data;
        setAccessToken(access);
        setRefreshToken(refresh);
        setUserPhoneNumber(resident_data?.phone_number || phoneNumber);

        // Save tokens and user data into context
        login({ access, refresh }, userData, user_type);
        console.log(response.data, "\n", user_type);

        if (user_type === "resident") {
          const profile_completed = userData.profile_completed;

          if (resident_data) {
            if (profile_completed === true) {
              console.log(user_type);
              setIsRedirecting(true);
              setTimeout(() => {
                navigate("/resident-parcel-dashboard", { replace: true });
                setIsRedirecting(false);
              }, 2000);
            } else {
              console.log("SIGNIN TOKEN:-", access, refresh);
              setShowSignInModal(true);
            }
          } else if (member_data) {
            setIsRedirecting(true);
            setTimeout(() => {
              navigate("/resident-parcel-dashboard", { replace: true });
              setIsRedirecting(false);
            }, 2000);
          }
        } else if (user_type === "delivery") {
          const profile_completed = userData.profile_completed;

          if (profile_completed === true) {
            console.log(userData, "\n", user_type);
            console.log("Final session:", session);
            localStorage.setItem("currentDeliveryStep", 1);
            updateDeliveryField("da_id", userData.id);
            updateDeliveryField("da_name", userData.da_name);
            updateDeliveryField("login_id", userData.login_id);
            updateDeliveryField("phone_number", userData.phone_number);
            updateDeliveryField("created_at", userData.created_at);
            updateDeliveryField("updated_at", userData.updated_at);
            updateDeliveryField("finished_at", userData.finished_at);
            updateDeliveryField("company_nm", userData.company?.company_name);
            updateDeliveryField("company_img", userData.company?.company_img);

            navigate("/delivery-dashboard");
          } else {
            console.log("DATA Sign:--", login);
            setShowModal(true);
          }
        }
      }
    } catch (error) {
      console.log("DATA:", error.status);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Invalid OTP or server error.";
      setIsOtpValid(errorMessage);
      setSuccessMessage(""); // ← Add this line
      setApiError(` ${error.response?.data?.message || ""}`);

      if (errorMessage.toLowerCase().includes("resident not found")) {
        // Handle specific error case
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-3 sm:p-4">
      <div className="relative w-full max-w-[400px] sm:max-w-[480px] bg-white rounded-[14px] px-4 sm:px-8 py-4 sm:py-10 shadow-lg overflow-y-auto max-h-[98vh] sm:max-h-[95vh] flex flex-col">
        <button
          className="absolute top-2 sm:top-4 right-2 sm:right-4 text-gray-500 hover:text-gray-700 text-lg sm:text-xl w-6 h-6 flex items-center justify-center"
          onClick={onClose}
          aria-label="Close login modal"
        >
          ✕
        </button>

        <div className="text-center mb-4 sm:mb-6 mt-2 sm:mt-0">
          <h1 className="text-[#312E2D] font-alata font-normal text-[13px] sm:text-[14.23px] leading-[100%] tracking-[0%] uppercase mb-2 sm:mb-3">
            LOG IN YOUR ACCOUNT
          </h1>

          <p className="text-[#666] text-[14px] sm:text-[16px] leading-[1.5] px-2 sm:px-0">
            Choose User Type:
          </p>

          <div className="flex justify-center gap-4 my-4">
            <button
              className={`flex items-center gap-2 px-6 py-2 rounded-full text-sm font-semibold ${
                user_type === "resident"
                  ? "bg-[#0086FF] text-white"
                  : "bg-[#E0E0E0] text-black"
              }`}
              onClick={() => setUserType("resident")}
            >
              🧑‍ RESIDENT
            </button>
            <button
              className={`flex items-center gap-2 px-6 py-2 rounded-full text-sm font-semibold ${
                user_type === "delivery"
                  ? "bg-[#000] text-white"
                  : "bg-[#E0E0E0] text-black"
              }`}
              onClick={() => setUserType("delivery")}
            >
              📦 DELIVERY
            </button>
          </div>

          <div className="flex items-center border border-[#D9D9D9] rounded-lg overflow-hidden mb-2 mt-4">
            <div className="px-3 py-3 border-r border-[#D9D9D9] bg-white text-[#1E1E1E] text-base sm:text-lg">
              +91
            </div>
            <input
              type="tel"
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={handlePhoneChange}
              maxLength={10}
              className="flex-1 py-3 px-4 text-base sm:text-lg outline-none disabled:bg-gray-100 placeholder:text-[#999]"
            />
          </div>
        </div>

        {phoneNumberError && (
          <p className="text-red-500 text-sm sm:text-sm mb-3 sm:mb-4 px-1">
            ❌ {phoneNumberError}
          </p>
        )}
        {apiError && (
          <p className="text-red-500 text-sm sm:text-sm mb-3 sm:mb-4 px-1">
            ❌ {apiError}
          </p>
        )}
        {successMessage && (
          <p className="text-green-600 text-sm sm:text-sm mb-3 sm:mb-4 px-1">
            ✅ {successMessage}
          </p>
        )}

        {!isOtpSent && (
          <div className="text-center mb-4 sm:mb-6">
            <button
              onClick={handleSendOtp}
              disabled={isLoading || phoneNumber.length !== 10}
              className="text-[#0086FF] font-dongle text-2xl disabled:opacity-50 hover:text-[#0066CC] transition-colors"
            >
              {isLoading ? "Sending..." : "SEND OTP >"}
            </button>
          </div>
        )}

        {isOtpSent && (
          <div className="text-center mb-4 sm:mb-6">
            {isResendDisabled ? (
              <button
                disabled
                className="text-[#0086FF] font-dongle text-xl sm:text-2xl opacity-50"
              >
                Resend OTP in {otpResendTimer}s
              </button>
            ) : (
              <p className="text-center text-xs sm:text-sm text-[#1E1E1E]">
                Didn't receive a code?{" "}
                <button
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className="text-[#004990] font-medium disabled:opacity-50 underline hover:text-[#003366] transition-colors text-sm sm:text-sm"
                >
                  {isLoading ? "Sending..." : "Resend"}
                </button>
              </p>
            )}
          </div>
        )}

        {/* {isOtpSent && ( */}
        <div
          className="flex justify-between gap-1 sm:gap-3 mb-4 sm:mb-6 px-1 sm:px-0"
          onPaste={handleOtpPaste} // Container-level paste handler
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onPaste={(e) => handleIndividualPaste(e, index)} // Add this
              onFocus={(e) => {
                activeInputRef.current = index;
                e.target.select();
              }}
              onChange={(e) => handleOtpChange(e, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              className="w-10 h-10 sm:w-12 sm:h-12 text-center border border-[#D9D9D9] rounded-lg text-[18px] sm:text-lg focus:outline-[#004990] focus:border-[#004990] transition-colors font-medium"
              disabled={isLoading}
            />
          ))}
        </div>
        {/* )} */}

        {otpError && (
          <p className="text-red-500 text-xs sm:text-sm text-center mb-3 sm:mb-4 px-1">
            ❌ {otpError}
          </p>
        )}

        {/* <div className="mt-auto pt-3 sm:pt-4 border-t border-[#D9D9D9]">
          <label className="text-[#1E1E1E] text-xs sm:text-sm flex items-start justify-center space-x-2 cursor-pointer px-1">
            <input
              type="checkbox"
              checked={isTermsAccepted}
              onChange={(e) => setIsTermsAccepted(e.target.checked)}
              className="w-4 h-4 sm:w-4 sm:h-4 mt-0.5 sm:mt-1 flex-shrink-0"
            />
            <span className="text-left leading-tight">
              I have read and accept{" "}
              <button className="text-[#004990] underline hover:text-[#003366] inline">
                Terms of Use
              </button>{" "}
              &{" "}
              <button className="text-[#004990] underline hover:text-[#003366] inline">
                Privacy Policy
              </button>
            </span>
          </label>
        </div> */}

        {/* Login Button */}
        {/* {isOtpSent && ( */}
        <button
          onClick={handleLogin}
          disabled={isLoading || otp.some((digit) => !digit)}
          className="w-full py-3 sm:py-3 bg-[#004990] text-white text-xs sm:text-sm font-semibold rounded-md hover:bg-[#003366] transition disabled:opacity-50 disabled:cursor-not-allowed mt-5 sm:mt-4 mb-4 sm:mb-6"
        >
          {isLoading ? "Verifying..." : "LOG IN"}
        </button>
        {/* )} */}

        {/* Popup Modal Signup Form for Delivery*/}
        {showModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center px-4">
            <div className="relative w-full max-w-[480px] h-[550px] bg-white rounded-[14px] px-4 sm:px-8 py-6 sm:py-10 shadow-lg max-h-[95vh] flex flex-col justify-between">
              {/* Close Button */}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-5 text-xl font-bold text-gray-600 hover:text-red-500"
              >
                ✕
              </button>

              {/* Scrollable Content Section */}
              <div className="overflow-y-auto pr-2 mt-8">
                <h2 className="text-xl font-semibold text-center mb-6">
                  DELIVERY ACCOUNT
                </h2>

                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring"
                />

                <div className="flex mb-6">
                  <span className="px-3 py-2 whitespace-nowrap border border-r-0 rounded-l-lg bg-gray-100 text-gray-600 text-sm sm:text-base">
                    +91
                  </span>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={phoneNumber}
                    maxLength={10}
                    onChange={handlePhoneChange}
                    className="min-w-0 flex-1 px-3 py-2 border rounded-r-lg focus:outline-none focus:ring text-sm sm:text-base"
                  />
                </div>

                <button
                  onClick={handleCreateAccount}
                  className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  CREATE ACCOUNT
                </button>
              </div>

              {/* Fixed Bottom: Terms and Privacy */}
              {/* <div className="mt-6 text-xs text-center text-gray-500">
                By signing up you agree to our{" "}
                <a href="#" className="text-blue-600 underline">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href="#" className="text-blue-600 underline">
                  Privacy Policy
                </a>
                .
              </div> */}
            </div>
          </div>
        )}
      </div>
      {showSignInModal && (
        <SignUpForm
          onClose={() => setShowSignInModal(false)}
          access={accessToken}
          refresh={refreshToken}
          phoneNumber={userPhoneNumber}
        />
      )}
    </div>
  );
};

export default SignInForm;
